import {
  BatteryFull,
  BatteryLow,
  BatteryWarning,
  Hourglass,
  Skull,
  Sparkles,
  type LucideIcon,
} from "lucide-react";
import { useMemo } from "react";
import {
  ContextMenu,
  ContextMenuHeader,
  ContextMenuRow,
  ContextMenuSeparator,
  ContextMenuSubmenuRow,
} from "./ContextMenu";
import { useChainStore } from "../store/chainStore";
import { useConnectionSaver } from "../graph/useConnectionSaver";
import {
  useStaticSystemsById,
  useWhDestinationByCode,
} from "../hooks/useStaticSystems";
import { CLASS_LABEL } from "../lib/systemClass";
import type {
  ConnectionDirection,
  ConnectionLifetime,
  ConnectionMassState,
  Signature,
  WhClass,
} from "../types/chain";

interface ConnectionContextMenuProps {
  connectionId: string;
  x: number;
  y: number;
  onClose: () => void;
}

const LIFETIME_OPTIONS: {
  value: ConnectionLifetime;
  label: string;
  Icon: LucideIcon;
}[] = [
  { value: "fresh", label: "Lifetime: Fresh", Icon: Sparkles },
  { value: "EOL", label: "Lifetime: EOL", Icon: Hourglass },
  { value: "VEOL", label: "Lifetime: VEOL", Icon: Skull },
];

const MASS_OPTIONS: {
  value: ConnectionMassState;
  label: string;
  Icon: LucideIcon;
}[] = [
  { value: "stable", label: "Mass: Stable", Icon: BatteryFull },
  { value: "destab", label: "Mass: Destab", Icon: BatteryLow },
  { value: "critical", label: "Mass: Critical", Icon: BatteryWarning },
];

interface SigEntry {
  sig: Signature;
  side: "from" | "to";
}

function ClassText({ cls }: { cls: WhClass | "K162" }) {
  if (cls === "K162") {
    return (
      <span className="context-menu__wh-class context-menu__wh-class--k162">
        ?
      </span>
    );
  }
  return (
    <span className={`context-menu__wh-class system-class--${cls}`}>
      {CLASS_LABEL[cls]}
    </span>
  );
}

function destinationClass(
  code: string,
  resolve: (code: string) => WhClass | null,
): WhClass | "K162" {
  if (code === "K162") return "K162";
  return resolve(code) ?? "K162";
}

export function ConnectionContextMenu({
  connectionId,
  x,
  y,
  onClose,
}: ConnectionContextMenuProps) {
  const connection = useChainStore((s) =>
    s.connections.find((c) => c.id === connectionId),
  );
  const fromSystem = useChainStore((s) =>
    connection
      ? (s.systems.find((sys) => sys.id === connection.from) ?? null)
      : null,
  );
  const toSystem = useChainStore((s) =>
    connection
      ? (s.systems.find((sys) => sys.id === connection.to) ?? null)
      : null,
  );
  const updateConnection = useChainStore((s) => s.updateConnection);
  const saver = useConnectionSaver();
  const { byId: staticById } = useStaticSystemsById();
  const { forCode: destinationFor } = useWhDestinationByCode();

  const fromStatic = connection ? staticById(connection.from) : undefined;
  const toStatic = connection ? staticById(connection.to) : undefined;

  const fromSigs: SigEntry[] = useMemo(
    () =>
      (fromSystem?.signatures ?? [])
        .filter((sig) => sig.group === "wormhole" && sig.wh_type)
        .map((sig) => ({ sig, side: "from" as const })),
    [fromSystem],
  );
  const toSigs: SigEntry[] = useMemo(
    () =>
      (toSystem?.signatures ?? [])
        .filter((sig) => sig.group === "wormhole" && sig.wh_type)
        .map((sig) => ({ sig, side: "to" as const })),
    [toSystem],
  );

  if (!connection) return null;

  const setLifetime = (lifetime: ConnectionLifetime) => {
    if (lifetime === connection.lifetime) {
      onClose();
      return;
    }
    updateConnection(connection.id, { lifetime });
    saver.enqueue(connection.id, { lifetime });
    onClose();
  };

  const setMass = (mass: ConnectionMassState) => {
    if (mass === connection.mass) {
      onClose();
      return;
    }
    updateConnection(connection.id, { mass });
    saver.enqueue(connection.id, { mass });
    onClose();
  };

  const pickSignature = (entry: SigEntry) => {
    const code = entry.sig.wh_type;
    if (!code) return;
    const isK162 = code === "K162";
    const k162Side: "from" | "to" = isK162
      ? entry.side
      : entry.side === "from"
        ? "to"
        : "from";
    const direction: ConnectionDirection = k162Side === "to" ? "out" : "in";
    updateConnection(connection.id, { wh_type: code, direction });
    saver.enqueue(connection.id, { wh_type: code, direction });
    onClose();
  };

  const bothContribute = fromSigs.length > 0 && toSigs.length > 0;
  const anyContribute = fromSigs.length > 0 || toSigs.length > 0;

  const renderSigRow = (entry: SigEntry) => {
    const code = entry.sig.wh_type!;
    const cls = destinationClass(code, destinationFor);
    const prefix =
      code === "K162" ? entry.sig.scan_id.slice(0, 3).toUpperCase() : null;
    return (
      <ContextMenuRow
        key={`${entry.side}-${entry.sig.scan_id}`}
        onClick={() => pickSignature(entry)}
        active={connection.wh_type === code}
      >
        <ClassText cls={cls} />
        <span>{code}</span>
        {prefix && (
          <span className="context-menu__sig-prefix">{prefix}</span>
        )}
      </ContextMenuRow>
    );
  };

  const renderFlatSigs = () => {
    const list = fromSigs.length > 0 ? fromSigs : toSigs;
    return list.map(renderSigRow);
  };

  const renderSplitSigs = () => {
    const fromName = fromStatic?.name ?? connection.from;
    const toName = toStatic?.name ?? connection.to;
    return (
      <>
        <ContextMenuHeader>{fromName}</ContextMenuHeader>
        {fromSigs.map(renderSigRow)}
        <ContextMenuSeparator />
        <ContextMenuHeader>{toName}</ContextMenuHeader>
        {toSigs.map(renderSigRow)}
      </>
    );
  };

  return (
    <ContextMenu x={x} y={y} onClose={onClose}>
      {LIFETIME_OPTIONS.map((opt) => (
        <ContextMenuRow
          key={opt.value}
          onClick={() => setLifetime(opt.value)}
          active={connection.lifetime === opt.value}
        >
          <opt.Icon size={14} aria-hidden />
          {opt.label}
        </ContextMenuRow>
      ))}
      <ContextMenuSeparator />
      {MASS_OPTIONS.map((opt) => (
        <ContextMenuRow
          key={opt.value}
          onClick={() => setMass(opt.value)}
          active={connection.mass === opt.value}
        >
          <opt.Icon size={14} aria-hidden />
          {opt.label}
        </ContextMenuRow>
      ))}
      <ContextMenuSeparator />
      <ContextMenuSubmenuRow label="Signature" disabled={!anyContribute}>
        {bothContribute ? renderSplitSigs() : renderFlatSigs()}
      </ContextMenuSubmenuRow>
    </ContextMenu>
  );
}
